import { Button, Empty } from 'antd';
import { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { LocationChooser } from 'components/choosers';
import { location, make, model, name, status } from 'components/data/turbines';
import DataTable from 'components/DataTable';
import {
  type AtlasGqlGetTurbineListChooserQuery,
  useGetTurbineListChooserLazyQuery,
} from 'types/atlas-graphql';
import { TABLE_SCROLL_WIDTH_SMALL } from 'utils/constants';
import { SelectableAction, useSelectableRecords } from 'utils/selectable';
import { notEmpty } from 'utils/types';

const Control = styled.div`
  .ant-select {
    min-width: 200px;
  }
`;

const columns = [name, location, status, make, model];

interface BulkTurbineChooserProps {
  onChange: (turbines: any[]) => void;
}

const BulkTurbineChooser: FC<BulkTurbineChooserProps> = ({ onChange }) => {
  const { t } = useTranslation();
  const [locationId, setLocationId] = useState<string | undefined>(undefined);
  const [turbines, setTurbines] = useState<AtlasGqlGetTurbineListChooserQuery['turbines']>([]);
  const { selected, rowSelection, onClearSelection } = useSelectableRecords();
  const [getTurbines, { loading }] = useGetTurbineListChooserLazyQuery();

  const handleAdd = useCallback(() => onChange(selected), [selected]);

  const handleLocationChange = useCallback((value: string | string[]) => {
    setLocationId(value instanceof Array ? value[0] : value);
  }, []);

  useEffect(() => {
    if (locationId) {
      getTurbines({
        variables: { filterBy: [{ key: 'siteId', values: [locationId] }] },
      }).then((result) => {
        if (result.data) {
          setTurbines(result.data.turbines?.filter(notEmpty) ?? []);
        }
      });
    } else {
      setTurbines([]);
    }
  }, [locationId, getTurbines]);

  return (
    <DataTable
      data-testid="bulk-turbine-chooser"
      loading={loading}
      scrollWidth={TABLE_SCROLL_WIDTH_SMALL}
      locale={{
        emptyText: (
          <Empty
            description={
              locationId && !turbines?.length && !loading
                ? t('choosers.noTurbinesFound', 'No Turbines found')
                : t('choosers.selectASite', 'Select a Site...')
            }
          />
        ),
      }}
      size="small"
      columns={columns}
      data={turbines ?? undefined}
      rowSelection={rowSelection}
      controls={
        <Control>
          <LocationChooser value={locationId} onChange={handleLocationChange} disabled={loading} />
        </Control>
      }
      actions={
        <SelectableAction selected={selected} onClear={onClearSelection}>
          <Button type="primary" disabled={!selected.length} onClick={handleAdd}>
            {t('common.add', 'Add')}
          </Button>
        </SelectableAction>
      }
    />
  );
};

export default BulkTurbineChooser;
